import type {
  CampaignAnalyticsKpi,
  CampaignAnalyticsKpiKey,
  CampaignAnalyticsTrendPoint,
} from "@adpilot/shared";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { filterSwitcherConnections, marketplaceCode } from "@/components/layout/shop-scope-select";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import { fetchCampaignAnalytics } from "@/lib/dashboard-api";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import type { Shop } from "@/stores/auth";

/** Categorical series tokens — the card caps the comparison at this many shops. */
export const SHOP_COMPARISON_COLORS = [
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
] as const;
export const SHOP_COMPARISON_MAX_SHOPS = SHOP_COMPARISON_COLORS.length;

export const SHOP_COMPARISON_METRICS = [
  "acos",
  "roas",
  "spend",
  "sales",
  "orders",
  "clicks",
  "impressions",
  "ctr",
  "cvr",
  "cpc",
  "cpa",
] as const satisfies readonly CampaignAnalyticsKpiKey[];
export type ShopComparisonMetric = (typeof SHOP_COMPARISON_METRICS)[number];

export const SHOP_COMPARISON_METRIC_FORMAT: Record<
  ShopComparisonMetric,
  CampaignAnalyticsKpi["format"]
> = {
  acos: "percent",
  roas: "number",
  spend: "currency",
  sales: "currency",
  orders: "number",
  clicks: "number",
  impressions: "number",
  ctr: "percent",
  cvr: "percent",
  cpc: "currency",
  cpa: "currency",
};

export interface ShopComparisonShop {
  shopId: string;
  name: string;
  /** Marketplace country code ("US", "JP"…); null until the accounts query lands. */
  marketplace: string | null;
}

export interface ShopComparisonSeries {
  shopId: string;
  currency: string | null;
  dataThrough: string | null;
  trend: CampaignAnalyticsTrendPoint[];
}

/** Scope shops with their marketplace code from the Amazon accounts query. */
export function useShopComparisonShops(
  workspaceShops: Shop[],
  scopeShopIds: string[],
  workspaceId: string | null,
): ShopComparisonShop[] {
  // Same key as the header shop-scope picker, so this is normally a cache hit.
  const accounts = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  return useMemo(() => {
    const marketplaces = new Map(
      filterSwitcherConnections(accounts.data?.connections ?? [], workspaceId).flatMap(
        (connection) =>
          connection.shops.map((shop) => [shop.shopId, marketplaceCode(shop.marketplaceName)]),
      ),
    );
    const inScope = new Set(scopeShopIds);
    return workspaceShops
      .filter((shop) => inScope.has(shop.shopId))
      .map((shop) => ({
        shopId: shop.shopId,
        name: shop.shopName,
        marketplace: marketplaces.get(shop.shopId) ?? null,
      }));
  }, [accounts.data, scopeShopIds, workspaceId, workspaceShops]);
}

interface ShopComparisonSeriesScope {
  workspaceId: string | null;
  shopIds: string[];
  startDate: string;
  endDate: string;
  timeZone: ReportingTimeZone;
  asins?: string[];
}

/**
 * One weekly analytics read per selected shop. The analytics endpoint only
 * aggregates its shop scope, so per-shop lines need one request each; the card
 * caps the selection at four, and each shop's result is cached under its own
 * key, so toggling a shop back in is a cache hit.
 */
export function useShopComparisonSeries(scope: ShopComparisonSeriesScope) {
  return useQueries({
    queries: scope.shopIds.map((shopId) => ({
      queryKey: [
        "dashboard",
        "shop-comparison",
        scope.workspaceId,
        shopId,
        scope.startDate,
        scope.endDate,
        scope.timeZone,
        (scope.asins ?? []).join(","),
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchCampaignAnalytics(
          {
            range: { type: "custom", startDate: scope.startDate, endDate: scope.endDate },
            timeZone: scope.timeZone,
            shopIds: [shopId],
            granularity: "week",
            delivery: "all",
            search: "",
            policy: [],
            run: [],
            asins: scope.asins,
          },
          signal,
        ),
      staleTime: 60_000,
    })),
    combine: (results) => ({
      series: results.flatMap((result, index): ShopComparisonSeries[] => {
        const shopId = scope.shopIds[index];
        if (!shopId || !result.data) return [];
        return [
          {
            shopId,
            currency: result.data.currency,
            dataThrough: result.data.dataThrough,
            trend: result.data.trend,
          },
        ];
      }),
      isLoading: results.some((result) => result.isLoading),
      isFetching: results.some((result) => result.isFetching),
      isError: results.some((result) => result.isError),
      refetch: () => {
        for (const result of results) if (result.isError) void result.refetch();
      },
    }),
  });
}

/** Keeps the previous order (and so each shop's color) and appends new picks. */
export function mergeShopSelection(previous: readonly string[], next: readonly string[]): string[] {
  const nextSet = new Set(next);
  const kept = previous.filter((shopId) => nextSet.has(shopId));
  const added = next.filter((shopId) => !previous.includes(shopId));
  return [...kept, ...added].slice(0, SHOP_COMPARISON_MAX_SHOPS);
}

const DAY_MS = 86_400_000;

/**
 * A week is drawn dashed/hollow when it does not hold seven full days of data:
 * clipped by the range, partially synced, or running past the latest synced day
 * (days after `dataThrough` are excluded upstream, so status alone says "complete").
 */
export function isIncompleteWeek(
  point: Pick<CampaignAnalyticsTrendPoint, "periodStart" | "periodEnd" | "dataStatus">,
  dataThrough: string | null,
): boolean {
  if (point.dataStatus === "partial") return true;
  const days = (Date.parse(point.periodEnd) - Date.parse(point.periodStart)) / DAY_MS + 1;
  if (days < 7) return true;
  return dataThrough != null && point.periodEnd > dataThrough;
}

export interface ShopComparisonRow {
  periodStart: string;
  periodEnd: string;
  [key: string]: string | number | boolean | null;
}

export const seriesKey = (index: number) => `value${index}`;
export const solidKey = (index: number) => `solid${index}`;
export const dashKey = (index: number) => `dash${index}`;
export const incompleteKey = (index: number) => `incomplete${index}`;

/**
 * Flattens per-shop trends into one row per week. Each series gets three value
 * keys so one chart can draw solid segments between complete weeks, dashed
 * segments touching an incomplete week, and markers on every week.
 */
export function buildShopComparisonRows(
  orderedSeries: readonly (ShopComparisonSeries | undefined)[],
  metric: ShopComparisonMetric,
): ShopComparisonRow[] {
  const periods = new Map<string, string>();
  for (const series of orderedSeries)
    for (const point of series?.trend ?? []) periods.set(point.periodStart, point.periodEnd);
  const starts = [...periods.keys()].sort();
  const rows: ShopComparisonRow[] = starts.map((periodStart) => ({
    periodStart,
    periodEnd: periods.get(periodStart) ?? periodStart,
  }));
  orderedSeries.forEach((series, index) => {
    const byStart = new Map(series?.trend.map((point) => [point.periodStart, point]) ?? []);
    const values = rows.map((row) => byStart.get(row.periodStart)?.[metric] ?? null);
    const incomplete = rows.map((row) => {
      const point = byStart.get(row.periodStart);
      return point ? isIncompleteWeek(point, series?.dataThrough ?? null) : false;
    });
    rows.forEach((row, rowIndex) => {
      const value = values[rowIndex] ?? null;
      const touchesIncomplete =
        incomplete[rowIndex] || incomplete[rowIndex - 1] || incomplete[rowIndex + 1];
      row[seriesKey(index)] = value;
      row[incompleteKey(index)] = incomplete[rowIndex] ?? false;
      row[solidKey(index)] = incomplete[rowIndex] ? null : value;
      row[dashKey(index)] = touchesIncomplete ? value : null;
    });
  });
  return rows;
}
